import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, CircularProgress, IconButton } from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CustomTablePagination from "../../../../../components/CustomTablePagination";
import ElevatedPaper from "../../../../../components/ElevatedPaper";
import Flex from "../../../../../components/Flex";
import PageLayout from "../../../../../components/PageLayout";
import Text from "../../../../../components/Text";
import { useRoleController } from "../../../../../components/useRoleController";
import { PROVIDER_PATHS } from "../../../../../constants/paths";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useGetEraByUuid } from "../../../../../queries/billings/era/use-get-era-by-uuid";
import { usePostEraClaims } from "../../../../../queries/billings/era/use-post-era-claims";
import {
  EraClaimRequest,
  EraClaimRequestClaimStatusEnum,
  EraClaimRequestPaidAsEnum,
  EraPostRequest,
  PatientProcedureCode,
} from "../../../../../services";
import { formatDate } from "../../../../../utils/date-time-utility";
import {
  DEFAULT_TEXT_FONT_WEIGHT,
  DEF_PAGINATION,
  DGT_Pagination,
  getIdFromObj,
  getValueFromObj,
  replaceUnderScoreAddSpace,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import EraClaimsRow, { EraClaimRow, claimKey } from "./EraClaimsRow";
import ViewEraEob from "./ViewEraEob";
import {
  ERA_POSTING_STATUS_VALUE,
  VALID_DENIAL_REASON_GROUPS,
} from "./constants";
import { buildCptForPost, round2, sumEraCptAmounts } from "./era-cpt";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);

/** `{ [uuid]: label }` dicts, read through getIdFromObj / getValueFromObj. */
type EraPartyRef = Record<string, string>;

type EraDetail = {
  billingProvider?: EraPartyRef;
  payer?: EraPartyRef;
  uuid?: string;
  eraDocNumber?: string;
  eraDate?: string;
  paymentMethod?: string;
  payerName?: string;
  billingProviderName?: string;
  renderingProviderName?: string;
  paymentDate?: string;
  statementDate?: string;
  checkEftNumber?: string;
  batchNumber?: string;
  instrumentNumber?: string;
  transactionId?: string;
  amount?: number;
  claims?: EraClaimRow[];
  pendingClaims?: EraClaimRow[];
};

const HeaderField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box sx={{ px: 2, py: 1, minWidth: 0, flex: "1 1 0" }}>
    <Text variant="caption" color="text.secondary" sx={{ display: "block" }}>
      {label}
    </Text>
    <Text fontWeight={DEFAULT_TEXT_FONT_WEIGHT} noWrap>
      {value ?? "-"}
    </Text>
  </Box>
);

const formatCurrency = (n: number | undefined) =>
  `$ ${Number(n ?? 0).toFixed(2)}`;

// Procedure lines go back to the backend with the amounts it reported; posting
// only stamps each line's posted state and posting status.
const withCptPostingAmounts = (
  pcs: PatientProcedureCode[] | undefined,
  claimGroupCode: string | undefined
): PatientProcedureCode[] | undefined =>
  pcs?.map((pc) => buildCptForPost(pc, claimGroupCode, Boolean(pc.posted)));

const EraDetailPage = () => {
  const navigate = useNavigate();
  const { eraUuid: eraUuidParam } = useParams<{ eraUuid: string }>();
  const [searchParams] = useSearchParams();
  const eraUuid = eraUuidParam ?? searchParams.get("eraUuid") ?? undefined;

  const showSnackbar = useSnackbar();
  const viewEobDisclosure = useDisclosure();

  const { hasUpdate: hasPostPayment } = useRoleController("Generate Claims");

  const { data, isFetching } = useGetEraByUuid(eraUuid, !!eraUuid);
  const era = (data as EraDetail | undefined) ?? undefined;

  const backendClaims =
    (era?.claims?.length ? era?.claims : era?.pendingClaims) ?? [];

  const [selectedClaimIds, setSelectedClaimIds] = useState<Set<string>>(
    new Set()
  );
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  // ECRH-5475 — the ERA get API returns read-only claims, so the user's CPT
  // Denied / Denial Code / Comments edits are held here as an overlay keyed by
  // claim + CPT and merged onto the backend claims for both render and post.
  const [cptDenialEdits, setCptDenialEdits] = useState<
    Record<string, Partial<PatientProcedureCode>>
  >({});

  const cptEditKey = (
    claim: EraClaimRow,
    pc: PatientProcedureCode,
    i: number
  ) => `${claimKey(claim)}::${pc.uuid ?? i}`;

  const claims: EraClaimRow[] = backendClaims.map((c) => ({
    ...c,
    patientProcedureCodes: (c.patientProcedureCodes ?? []).map((pc, i) => {
      const edit = cptDenialEdits[cptEditKey(c, pc, i)];
      return edit ? { ...pc, ...edit } : pc;
    }),
  }));

  const pageStart = pagination.page * pagination.pageSize;
  const pageEnd = pageStart + pagination.pageSize;
  const pagedClaims = claims.slice(pageStart, pageEnd);

  const handleEditCpt = (
    claim: EraClaimRow,
    pcIndex: number,
    field: "denied" | "denialCode" | "comments",
    value: boolean | string
  ) => {
    const pc = claim.patientProcedureCodes?.[pcIndex];
    if (!pc) return;
    const key = cptEditKey(claim, pc, pcIndex);
    setCptDenialEdits((prev) => {
      const next: Partial<PatientProcedureCode> = {
        ...prev[key],
        [field]: value,
      };
      // Clearing the Denied flag drops the denial code that went with it.
      if (field === "denied" && value === false) next.denialCode = "";
      return { ...prev, [key]: next };
    });
  };

  const { mutateAsync: postEraClaims } = usePostEraClaims();

  const validateDenialBeforePost = (claim: EraClaimRow): string | null => {
    const status = (claim.claimStatus ?? "").toUpperCase();
    if (status !== "DENIED" && status !== "REJECTED") return null;
    const group = (claim.groupCode ?? "").toUpperCase();
    if (
      !group ||
      !VALID_DENIAL_REASON_GROUPS.includes(
        group as (typeof VALID_DENIAL_REASON_GROUPS)[number]
      )
    ) {
      return `Denied claim ${
        claim.claimId ?? claim.payersClaimId ?? ""
      } missing valid denial reason group code (CO, PR, OA, PI).`;
    }
    return null;
  };

  const resolveProviderName = (c: EraClaimRow): string | undefined =>
    (c as { providerName?: string }).providerName ??
    (c as { provider?: string }).provider ??
    era?.renderingProviderName ??
    era?.billingProviderName ??
    (era?.billingProvider
      ? (getValueFromObj(era.billingProvider) as string | undefined)
      : undefined);

  const resolvePayerName = (c: EraClaimRow): string | undefined =>
    (c as { payerName?: string }).payerName ??
    era?.payerName ??
    (era?.payer
      ? (getValueFromObj(era.payer) as string | undefined)
      : undefined);

  const resolveDateOfService = (c: EraClaimRow): string | undefined =>
    (c as { dateOfService?: string }).dateOfService ??
    (c as { dos?: string }).dos ??
    (c as { serviceDate?: string }).serviceDate;

  const mapClaimToRequest = (c: EraClaimRow): EraClaimRequest => {
    // Claim-level money is the sum of the claim's procedure lines, so the claim
    // header can never disagree with the sub-table totals shown to the user.
    const t = sumEraCptAmounts(c.patientProcedureCodes, c.groupCode);

    return {
      ...c,
      // The ERA row carries the claim's uuid inside the `claim` map rather than
      // as `claimId`; without this the DTO's claimId would go up undefined.
      claimId: c.claimId ?? (c.claim ? getIdFromObj(c.claim) : undefined),
      payersClaimId: (c.payersClaimId ?? "") as string,
      dateOfService: resolveDateOfService(c),
      providerName: resolveProviderName(c),
      payerName: resolvePayerName(c),
      chargeAmount: t.bill ?? 0,
      insurancePaid: t.insurancePaid ?? 0,
      insuranceAdjustment: round2((t.contractAdj ?? 0) + (t.otherAdj ?? 0)),
      patientResponsibility: t.patientResp ?? 0,
      allowedAmount: t.allowed ?? 0,
      paymentDate: ((c as { paymentDate?: string }).paymentDate ??
        "") as string,
      statementDate: (c as { statementDate?: string }).statementDate,
      claimStatus: (
        (c.claimStatus ?? "") as string
      ).toUpperCase() as EraClaimRequestClaimStatusEnum,
      statusCode: (c as { statusCode?: string }).statusCode,
      groupCode: c.groupCode,
      reasonCode: c.reasonCode,
      copay: t.coPay ?? 0,
      coInsurance: t.coIns ?? 0,
      deductible: t.deduct ?? 0,
      patientName: c.patientName,
      paidAs: (c.paidAs || undefined) as EraClaimRequestPaidAsEnum | undefined,
      patientProcedureCodes: withCptPostingAmounts(
        c.patientProcedureCodes,
        c.groupCode
      ),
    };
  };

  const buildPostRequest = (toPost: EraClaimRow[]): EraPostRequest => ({
    amount: era?.amount,
    checkEftNumber: era?.checkEftNumber,
    paymentMethod: era?.paymentMethod,
    eraDate: era?.eraDate,
    batchNumber: era?.batchNumber,
    instrumentNumber: era?.instrumentNumber,
    claims: toPost.map(mapClaimToRequest),
  });

  // ECRH-4700 — post a single CPT (service line) of a claim. Only the targeted
  // CPT is flagged `posted: true`; previously-posted lines are preserved. The
  // claim is marked posted with a derived `postingStatus` (Partially Paid until
  // every CPT is posted, then Paid). The full claim list is still sent so the
  // backend keeps its existing per-claim posting state.
  const handlePostCpt = (postClaim: EraClaimRow, pcIndex: number) => {
    if (!era?.uuid) return;

    const claimInd = claims.indexOf(postClaim);
    if (claimInd < 0) return;

    const pcs = postClaim.patientProcedureCodes ?? [];
    if (pcIndex < 0 || pcIndex >= pcs.length) return;
    if (pcs[pcIndex]?.posted) return; // already posted — no-op

    // ECRH-5475 — a CPT marked Denied cannot be posted without a denial code.
    const targetPc = pcs[pcIndex];
    if (targetPc?.denied && !(targetPc.denialCode ?? "").trim()) {
      showSnackbar({
        message: "Denial Code is required to post a denied CPT.",
        type: "error",
      });
      return;
    }

    const updatedPcs = pcs.map((pc, i) =>
      i === pcIndex ? { ...pc, posted: true } : pc
    );
    const postedCount = updatedPcs.filter((pc) => pc.posted).length;
    const allPosted =
      updatedPcs.length > 0 && postedCount === updatedPcs.length;

    const toPostClaim: EraClaimRow = {
      ...postClaim,
      patientProcedureCodes: updatedPcs,
      posted: true,
      // Claim-level posting status keeps the vocabulary the get API returns
      // ("Fully Posted" / "Partially Posted"); the posting DTO has no
      // claim-level postingStatus field to match against. Only the CPT lines
      // carry the PAID / UNPAID enum.
      postingStatus: allPosted
        ? ERA_POSTING_STATUS_VALUE.PAID
        : ERA_POSTING_STATUS_VALUE.PARTIALLY_PAID,
    };

    const err = validateDenialBeforePost(toPostClaim);
    if (err) {
      showSnackbar({ message: err, type: "error" });
      return;
    }

    const toPost = claims.toSpliced(claimInd, 1, toPostClaim);
    const eraPostRequest = buildPostRequest(toPost);

    showToastOnApiCall({
      apiCall: postEraClaims({
        uuid: era.uuid,
        eraPostRequest,
      }),
      showSnackbar,
      onSave: () => {
        setSelectedClaimIds((prev) => {
          const next = new Set(prev);
          if (allPosted) next.delete(claimKey(toPostClaim));
          return next;
        });
      },
    });
  };

  const toggleSelect = (key: string) =>
    setSelectedClaimIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleSelectAll = (selectAll: boolean) =>
    setSelectedClaimIds(() => {
      if (!selectAll) return new Set();
      const next = new Set<string>();
      claims.forEach((c) => {
        const isPosted =
          c.posted ||
          c.postedAt ||
          (c.claimStatus ?? "").toUpperCase() === "POSTED";
        if (!isPosted) next.add(claimKey(c));
      });
      return next;
    });

  // const selectedClaims = claims.filter((c) =>
  //   selectedClaimIds.has(claimKey(c))
  // );

  // const handleClaimAction = (action: string, claim: EraClaimRow) => {
  //   const id = claim.claimId ?? claim.payersClaimId;
  //   if (action === "view_eob_details") {
  //     if (era?.uuid) viewEobDisclosure.open(era.uuid);
  //   } else if (action === "edit_eob") {
  //     if (id) {
  //       navigate(`${PROVIDER_PATHS.CLAIMS}/edit-claim?claimId=${id}`);
  //     }
  //   } else if (action === "post_payment") {
  //     handlePost([claim]);
  //   }
  // };

  if (!eraUuid) {
    return (
      <Box p={3}>
        <Text>No ERA selected.</Text>
      </Box>
    );
  }

  const docTitle =
    era?.eraDocNumber ?? era?.uuid?.split("-")?.[0]?.toUpperCase() ?? "-";

  // eraDate carries no time of day, so formatting one printed the same clock
  // time on every ERA. Show the date through the shared formatter the ERA list
  // already uses, so both screens agree on the timezone.
  const titleTimestamp = formatDate(era?.eraDate, undefined, "MMMM Do, YYYY");

  return (
    <ElevatedPaper>
      <PageLayout
        header={
          <Flex alignItems={"center"} gap={1} height={"100%"}>
            <Flex gap={1} height={"100%"} alignItems={"start"}>
              <IconButton
                size="small"
                onClick={() => navigate(PROVIDER_PATHS.BILLING + "era_eob/era")}
                sx={{ mt: 0.25 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Flex>
            <Flex
              gap={0.5}
              alignItems={"start"}
              flexDirection={"column"}
              width={"100%"}
            >
              <Text
                variant="h6"
                sx={{ fontWeight: 600, lineHeight: 1.2 }}
                mt={1}
              >
                {docTitle}
              </Text>
              <Flex
                gap={1}
                width={"100%"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                {titleTimestamp && (
                  <Text variant="caption" color="text.secondary">
                    {titleTimestamp}
                  </Text>
                )}

                {/* <Flex gap={1}>
                {selectedClaims.length > 0 && (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Text variant="body2" color="text.secondary">
                      {selectedClaims.length} claim
                      {selectedClaims.length === 1 ? "" : "s"} selected
                    </Text>
                    <StyledButtonV2
                      label={`Bulk Post (${selectedClaims.length})`}
                      variant="contained"
                      disabled={!hasPostPayment}
                      onClick={() => handlePost(selectedClaims)}
                    />
                  </Stack>
                )}
              </Flex> */}
              </Flex>
            </Flex>
          </Flex>
        }
      >
        <Box
          sx={{
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 1,
            mb: 3,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Text
              variant="caption"
              sx={{
                color: "text.secondary",
                letterSpacing: 0.6,
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              ERA Summary
            </Text>
          </Box>
          {isFetching && !era ? (
            <Flex alignItems="center" justifyContent="center" py={3}>
              <CircularProgress size={20} />
            </Flex>
          ) : (
            <Flex
              alignItems="stretch"
              sx={{
                "& > *:not(:last-child)": {
                  borderRight: "1px solid",
                  borderColor: "grey.200",
                },
                flexWrap: "wrap",
              }}
            >
              <HeaderField
                label="Payer"
                value={
                  era?.payer
                    ? `${getIdFromObj(era.payer) ?? ""} ${
                        getValueFromObj(era.payer) ?? ""
                      }`.trim() || "-"
                    : "-"
                }
              />
              <HeaderField
                label="Provider Name"
                value={
                  era?.renderingProviderName ??
                  era?.billingProviderName ??
                  (era?.billingProvider
                    ? getValueFromObj(era?.billingProvider)
                    : "-")
                }
              />
              <HeaderField
                label="Payment Method"
                value={replaceUnderScoreAddSpace(era?.paymentMethod ?? "-")}
              />
              <HeaderField
                label="Payment Date"
                value={
                  // era?.paymentDate
                  //   ? formatDate(era.paymentDate)
                  era?.eraDate ? formatDate(era.eraDate) : "-"
                }
              />
              <HeaderField
                label="Statement Date"
                value={
                  // era?.statementDate
                  //   ? formatDate(era.statementDate)
                  era?.eraDate ? formatDate(era.eraDate) : "-"
                }
              />
              <HeaderField
                label="Transaction ID"
                value={era?.transactionId ?? era?.checkEftNumber ?? "-"}
              />
              <HeaderField
                label="Total Payment Amount"
                value={formatCurrency(era?.amount)}
              />
            </Flex>
          )}
        </Box>

        {isFetching && claims.length === 0 ? (
          <Flex alignItems="center" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Flex>
        ) : (
          <>
            <EraClaimsRow
              claims={pagedClaims}
              selectedClaimIds={selectedClaimIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              onPostCpt={(claim, pcIndex) => handlePostCpt(claim, pcIndex)}
              onEditCpt={handleEditCpt}
              // onClaimAction={handleClaimAction}
              hasPostPermission={hasPostPayment}
            />
            <CustomTablePagination
              paginationModel={pagination}
              onPaginationModelChange={setPagination}
              rowCount={claims.length}
            />
          </>
        )}
      </PageLayout>
      <ViewEraEob
        disclosureHost={viewEobDisclosure}
        eraEobUuid={viewEobDisclosure.data}
      />
    </ElevatedPaper>
  );
};

export default EraDetailPage;
